import React, { useContext } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Slider from '@mui/material/Slider';
import { ColorPicker } from 'mui-color';
import AppContext from '../../context/AppContext';

function valueText(value) {
  return `${value}`;
}
const BarcodeSettings = () => {
  const { data, setData } = useContext(AppContext);
  return (
    <Accordion sx={{ width: '100%' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography color="primary" variant="body2">
          <strong>Barcode Options</strong>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack sx={{ width: 1 }} direction="column" spacing={2}>
          {/* Bar Width */}
          <Stack
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Bar Width</Typography>
            <Slider
              sx={{ width: 4 / 5 }}
              defaultValue={2}
              value={data.barcodeSettings.barWidth}
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    barWidth: newValue,
                  },
                })
              }
              getAriaValueText={valueText}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={1}
              max={10}
            />
          </Stack>
          {/* Bar Height */}
          <Stack
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Height</Typography>
            <Slider
              sx={{ width: 4 / 5 }}
              defaultValue={100}
              value={data.barcodeSettings.barHeight}
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    barHeight: newValue,
                  },
                })
              }
              getAriaValueText={valueText}
              valueLabelDisplay="auto"
              step={5}
              marks
              min={10}
              max={150}
            />
          </Stack>
          {/* Margin */}
          <Stack
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Margin</Typography>
            <Slider
              sx={{ width: 4 / 5 }}
              defaultValue={13}
              value={data.barcodeSettings.margin}
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    margin: newValue,
                  },
                })
              }
              getAriaValueText={valueText}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={0}
              max={25}
            />
          </Stack>
          {/* Background */}
          <Stack
            spacing={2}
            justifyContent="start"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 2.1, width: 'auto' }}>Background</Typography>
            <ColorPicker
              value={data.barcodeSettings.background}
              defaultValue={data.barcodeSettings.background}
              onChange={(value) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    background: `#${value.hex}`,
                  },
                })
              }
            />
          </Stack>
          {/* Line Color */}
          <Stack
            spacing={2}
            justifyContent="start"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Line Color</Typography>
            <ColorPicker
              value={data.barcodeSettings.lineColor}
              defaultValue={data.barcodeSettings.lineColor}
              onChange={(value) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    lineColor: `#${value.hex}`,
                  },
                })
              }
            />
          </Stack>
          {/* Show Text */}
          <Stack
            spacing={2}
            justifyContent="start"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Show Text</Typography>
            <ToggleButtonGroup
              color="primary"
              value={`${data.barcodeSettings.showText === true}`}
              exclusive
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    showText: newValue.includes('true'),
                  },
                })
              }
            >
              <ToggleButton value="true">SHOW</ToggleButton>
              <ToggleButton value="false">HIDE</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {/* Text Align */}
          <Stack
            spacing={2}
            justifyContent="start"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Text Align</Typography>
            <ToggleButtonGroup
              color="primary"
              value={data.barcodeSettings.textAlign}
              exclusive
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    textAlign: newValue,
                  },
                })
              }
            >
              <ToggleButton value="left">LEFT</ToggleButton>
              <ToggleButton value="center">CENTER</ToggleButton>
              <ToggleButton value="right">RIGHT</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          {/* Font */}
          <Stack
            spacing={2}
            justifyContent="start"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 10, width: 'auto' }}>Font</Typography>
            <Select
              color="primary"
              variant="filled"
              value={data.barcodeSettings.font}
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    font: e.target.value,
                  },
                })
              }
            >
              <MenuItem value="monospace">
                <em>MonoSpace</em>
              </MenuItem>
              <MenuItem value="sans-serif">
                <em>Sans-Serif</em>
              </MenuItem>
              <MenuItem value="serif">
                <em>Serif</em>
              </MenuItem>
              <MenuItem value="cursive">
                <em>Cursive</em>
              </MenuItem>
            </Select>
          </Stack>
          {/* Font Size */}
          <Stack
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            direction="row"
          >
            <Typography sx={{ mr: 4, width: 'auto' }}>Font Size</Typography>
            <Slider
              sx={{ width: 4 / 5 }}
              defaultValue={20}
              onChange={(e, newValue) =>
                setData({
                  ...data,
                  barcodeSettings: {
                    ...data.barcodeSettings,
                    fontSize: newValue,
                  },
                })
              }
              getAriaValueText={valueText}
              valueLabelDisplay="auto"
              step={1}
              marks
              min={8}
              max={38}
            />
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default BarcodeSettings;
